import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { accountStore } from '@/lib/studyStorage';

export interface ProgressState {
  lastStudyDate?: string;
  xp: number;
  level: number;
  streakDays: number;
  longestStreak: number;
  weeklyGoal: number;
  weeklyProgress: number;
  totalQuestionsSolved: number;
  totalQuizzesCompleted: number;
  totalVideosWatched: number;
  totalTimeStudiedMinutes: number;
  learningStyle: string;
  subjectMastery: Record<string, SubjectMastery>;
  recentXpEvents: XpEvent[];
}

export interface SubjectMastery {
  subjectId: string;
  subjectName: string;
  masteryPercent: number;
  questionsSolved: number;
  quizzesCompleted: number;
  lessonsCompleted: number;
  accuracyRate: number;
}

export interface XpEvent {
  id: string;
  eventType: string;
  xpAmount: number;
  subjectId?: string;
  description: string;
  createdAt: string;
}


const defaultProgress: ProgressState = {
  xp: 0,
  level: 1,
  streakDays: 0,
  longestStreak: 0,
  weeklyGoal: 5,
  weeklyProgress: 0,
  totalQuestionsSolved: 0,
  totalQuizzesCompleted: 0,
  totalVideosWatched: 0,
  totalTimeStudiedMinutes: 0,
  learningStyle: 'balanced',
  subjectMastery: {},
  recentXpEvents: [],
};



function loadFromStorage(): ProgressState {
  try {
    const parsed = progressStore.read();
    if (parsed) {
      return { ...defaultProgress, ...parsed, subjectMastery: { ...defaultProgress.subjectMastery, ...(parsed.subjectMastery || {}) } };
    }
  } catch {
    // ignore
  }
  return { ...defaultProgress };
}

// Unowned legacy progress is left untouched; do not assign it to the next login.
const progressStore = accountStore<Partial<ProgressState> | null>('progress', () => null);

function saveToStorage(progress: ProgressState) {
  try {
    progressStore.write(progress);
  } catch {
    // ignore
  }
}

// XP needed per level: increases by 250 each level
export function xpForLevel(level: number): number {
  return level * 250 + (level - 1) * 150;
}

export function getLevelProgress(xp: number, level: number): number {
  const currentLevelXp = xpForLevel(level - 1);
  const nextLevelXp = xpForLevel(level);
  const progress = (xp - currentLevelXp) / (nextLevelXp - currentLevelXp);
  return Math.min(Math.max(progress, 0), 1);
}

interface ProgressContextValue {
  progress: ProgressState;
  addXp: (amount: number, eventType: string, description: string, subjectId?: string) => void;
  recordQuestionSolved: (subjectId: string, subjectName: string, accuracy?: number) => void;
  recordQuizCompleted: (subjectId: string, subjectName: string, score: number) => void;
  recordVideoWatched: (subjectId?: string) => void;
  recordTimeStudied: (minutes: number) => void;
  checkAndUpdateStreak: () => void;
  setLearningStyle: (style: string) => void;
  getXpForNextLevel: () => number;
  getLevelProgressPercent: () => number;
}

const ProgressContext = createContext<ProgressContextValue | null>(null);

export function ProgressProvider({ children }: { children: React.ReactNode }) {
  const [progress, setProgress] = useState<ProgressState>(loadFromStorage);
  const [xpPopup, setXpPopup] = useState<{ amount: number; description: string } | null>(null);

  useEffect(() => {
    saveToStorage(progress);
  }, [progress]);

  // Check streak on mount
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const lastStudy = progress.lastStudyDate;
    if (lastStudy && lastStudy !== today) {
      const lastDate = new Date(lastStudy);
      const todayDate = new Date(today);
      const diffDays = Math.floor((todayDate.getTime() - lastDate.getTime()) / 86400000);
      if (diffDays > 1) {
        setProgress((p) => ({ ...p, streakDays: 0 }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addXp = useCallback((amount: number, eventType: string, description: string, subjectId?: string) => {
    setProgress((p) => {
      const newXp = p.xp + amount;
      let newLevel = p.level;
      while (newXp >= xpForLevel(newLevel)) {
        newLevel += 1;
      }
      const newEvent: XpEvent = {
        id: `evt-${Date.now()}`,
        eventType,
        xpAmount: amount,
        subjectId,
        description,
        createdAt: new Date().toISOString(),
      };
      return {
        ...p,
        xp: newXp,
        level: newLevel,
        recentXpEvents: [newEvent, ...p.recentXpEvents.slice(0, 19)],
      };
    });
    setXpPopup({ amount, description });
    setTimeout(() => setXpPopup(null), 2200);
  }, []);

  const recordQuestionSolved = useCallback((subjectId: string, subjectName: string, accuracy = 100) => {
    const xpGain = 25 + Math.floor(Math.random() * 15);
    addXp(xpGain, 'question_solved', `Solved ${subjectName} problem`, subjectId);
    setProgress((p) => {
      const mastery = { ...p.subjectMastery };
      if (!mastery[subjectId]) {
        mastery[subjectId] = { subjectId, subjectName, masteryPercent: 0, questionsSolved: 0, quizzesCompleted: 0, lessonsCompleted: 0, accuracyRate: accuracy };
      }
      const subject = mastery[subjectId];
      const newQuestions = subject.questionsSolved + 1;
      const newAccuracy = Math.round((subject.accuracyRate * subject.questionsSolved + accuracy) / newQuestions);
      const newMastery = Math.min(100, subject.masteryPercent + Math.floor(Math.random() * 3) + 1);
      mastery[subjectId] = { ...subject, questionsSolved: newQuestions, accuracyRate: newAccuracy, masteryPercent: newMastery };
      return {
        ...p,
        totalQuestionsSolved: p.totalQuestionsSolved + 1,
        subjectMastery: mastery,
        lastStudyDate: new Date().toISOString().split('T')[0],
      };
    });
  }, [addXp]);

  const recordQuizCompleted = useCallback((subjectId: string, subjectName: string, score: number) => {
    const xpGain = 40 + Math.floor((score / 100) * 40);
    addXp(xpGain, 'quiz_completed', `Completed ${subjectName} quiz — ${score}%`, subjectId);
    setProgress((p) => {
      const mastery = { ...p.subjectMastery };
      if (!mastery[subjectId]) {
        mastery[subjectId] = { subjectId, subjectName, masteryPercent: 0, questionsSolved: 0, quizzesCompleted: 0, lessonsCompleted: 0, accuracyRate: score };
      }
      const subject = mastery[subjectId];
      mastery[subjectId] = {
        ...subject,
        quizzesCompleted: subject.quizzesCompleted + 1,
        masteryPercent: Math.min(100, subject.masteryPercent + 5),
        accuracyRate: Math.round((subject.accuracyRate * subject.quizzesCompleted + score) / (subject.quizzesCompleted + 1)),
      };
      return {
        ...p,
        totalQuizzesCompleted: p.totalQuizzesCompleted + 1,
        subjectMastery: mastery,
        weeklyProgress: Math.min(p.weeklyGoal, p.weeklyProgress + 1),
        lastStudyDate: new Date().toISOString().split('T')[0],
      };
    });
  }, [addXp]);

  const recordVideoWatched = useCallback((subjectId?: string) => {
    addXp(20, 'video_watched', 'Completed video lesson', subjectId);
    setProgress((p) => ({
      ...p,
      totalVideosWatched: p.totalVideosWatched + 1,
      totalTimeStudiedMinutes: p.totalTimeStudiedMinutes + 8,
      lastStudyDate: new Date().toISOString().split('T')[0],
    }));
  }, [addXp]);

  const recordTimeStudied = useCallback((minutes: number) => {
    setProgress((p) => ({
      ...p,
      totalTimeStudiedMinutes: p.totalTimeStudiedMinutes + minutes,
      lastStudyDate: new Date().toISOString().split('T')[0],
    }));
  }, []);

  const checkAndUpdateStreak = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    setProgress((p) => {
      if (p.lastStudyDate === today) return p;
      const lastDate = p.lastStudyDate ? new Date(p.lastStudyDate) : null;
      const todayDate = new Date(today);
      let newStreak = p.streakDays;
      if (!lastDate) {
        newStreak = 1;
      } else {
        const diffDays = Math.floor((todayDate.getTime() - lastDate.getTime()) / 86400000);
        if (diffDays === 1) {
          newStreak = p.streakDays + 1;
        } else if (diffDays > 1) {
          newStreak = 1;
        }
      }
      const longest = Math.max(p.longestStreak, newStreak);
      // Streak bonus every 7 days
      if (newStreak > 0 && newStreak % 7 === 0 && newStreak !== p.streakDays) {
        setTimeout(() => addXp(50 + newStreak * 2, 'streak_bonus', `${newStreak}-day streak bonus!`), 500);
      }
      return {
        ...p,
        streakDays: newStreak,
        longestStreak: longest,
        lastStudyDate: today,
      };
    });
  }, [addXp]);

  const setLearningStyle = useCallback((style: string) => {
    setProgress((p) => ({ ...p, learningStyle: style }));
  }, []);

  const getXpForNextLevel = useCallback(() => {
    return xpForLevel(progress.level);
  }, [progress.level]);

  const getLevelProgressPercent = useCallback(() => {
    return getLevelProgress(progress.xp, progress.level);
  }, [progress.xp, progress.level]);

  return (
    <ProgressContext.Provider
      value={{
        progress,
        addXp,
        recordQuestionSolved,
        recordQuizCompleted,
        recordVideoWatched,
        recordTimeStudied,
        checkAndUpdateStreak,
        setLearningStyle,
        getXpForNextLevel,
        getLevelProgressPercent,
      }}
    >
      {children}
      {/* XP Popup Overlay */}
      <AnimatePresence>
        {xpPopup && (
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[100] pointer-events-none"
          >
            <div className="bg-neutral-900 text-white px-5 py-3 rounded-2xl shadow-xl flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                <i className="ri-flashlight-line text-amber-400 text-sm" />
              </div>
              <div>
                <p className="text-sm font-bold">+{xpPopup.amount} XP</p>
                <p className="text-[11px] text-white/50">{xpPopup.description}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </ProgressContext.Provider>
  );
}

export function useProgress() {
  const ctx = useContext(ProgressContext);
  if (!ctx) {
    throw new Error('useProgress must be used within ProgressProvider');
  }
  return ctx;
}

export function getLevelTitle(level: number): string {
  if (level >= 20) return 'Master Scholar';
  if (level >= 15) return 'Expert Learner';
  if (level >= 10) return 'Advanced Student';
  if (level >= 7) return 'Dedicated Scholar';
  if (level >= 5) return 'Rising Star';
  if (level >= 3) return 'Curious Mind';
  return 'Fresh Starter';
}

export function getLearningStyleDescription(style: string): string {
  switch (style) {
    case 'visual': return 'Prefers diagrams, videos, and visual explanations';
    case 'fast': return 'Wants quick summaries and key takeaways';
    case 'detailed': return 'Enjoys thorough explanations with examples';
    case 'exam-prep': return 'Focused on exam technique and past paper style';
    case 'balanced': return 'Adapts between all learning styles';
    default: return 'Adapts to the topic';
  }
}